import { useFormContext } from 'react-hook-form'
import { useMemo } from 'react'
import Select from 'components/FormUtils/Select'
import useApiService from 'services/api'
import { useUsers } from 'services/users'
import { AutomationNoteManager } from './AutomationNoteManager'
import { AutomationAttachment } from 'models/automation'

const actionOptions = [
  { value: 'notify_user', label: 'Notify user' },
  { value: 'change_sales_stage', label: 'Change sales stage' },
  { value: 'change_account_owner', label: 'Change account owner' },
  { value: 'add_tag', label: 'Add tag' },
  { value: 'add_to_campaign', label: 'Add to campaign' },
  { value: 'remove_from_campaign', label: 'Remove from campaign' },
  { value: 'add_company_note', label: 'Add note to company' },
]

// Filter action options to prevent infinity loops
function getFilteredActionOptions(selectedTrigger: string | null | undefined) {
  return actionOptions.map((option) => {
    let disabled = false

    // Prevent infinity loop: if trigger is sales stage change, disable action sales stage change
    if (
      selectedTrigger === 'field_changed_sales_stage' &&
      option.value === 'change_sales_stage'
    ) {
      disabled = true
    }

    // Prevent infinity loop: if trigger is account owner change, disable action account owner change
    if (
      selectedTrigger === 'field_changed_account_owner' &&
      option.value === 'change_account_owner'
    ) {
      disabled = true
    }

    return {
      ...option,
      disabled,
    }
  })
}

interface ActionRendererProps {
  selectedAction: string | null
  selectedTrigger?: string | null
  existingAttachments?: AutomationAttachment[]
  automationId?: number
}

// Component for "Notify User" action - includes both dropdowns
function NotifyUserAction({
  selectedTrigger,
}: {
  selectedTrigger?: string | null
}) {
  const { control } = useFormContext()
  const { data: users } = useUsers()

  const userOptions = useMemo(() => {
    if (!users) return []
    return users.map((user) => ({
      value: user.id,
      label: `${user.first_name} ${user.last_name}`,
    }))
  }, [users])

  const filteredActionOptions = useMemo(
    () => getFilteredActionOptions(selectedTrigger),
    [selectedTrigger]
  )

  return (
    <>
      <Select
        name="action_type"
        control={control}
        placeholder="Choose action..."
        options={filteredActionOptions}
        className="flex-1"
      />
      <Select
        name="target_user_id"
        control={control}
        placeholder="Choose user..."
        options={userOptions}
        className="flex-1"
      />
    </>
  )
}

// Component for "Change Sales Stage" action - includes both dropdowns
function ChangeSalesStageAction({
  selectedTrigger,
}: {
  selectedTrigger?: string | null
}) {
  const { control } = useFormContext()
  const api = useApiService()
  const { data: salesStageOptions } = api.useGetSalesStageOptions()

  const filteredActionOptions = useMemo(
    () => getFilteredActionOptions(selectedTrigger),
    [selectedTrigger]
  )

  return (
    <>
      <Select
        name="action_type"
        control={control}
        placeholder="Choose action..."
        options={filteredActionOptions}
        className="flex-1"
      />
      <div className="flex items-center justify-center px-3 py-2 mt-3 bg-gray-100 rounded text-sm font-medium text-gray-700">
        TO
      </div>
      <Select
        name="target_stage_id"
        control={control}
        placeholder="Choose sales stage..."
        options={salesStageOptions}
        className="flex-1"
      />
    </>
  )
}

// Component for "Change Account Owner" action - includes action + TO + user
function ChangeAccountOwnerAction({
  selectedTrigger,
}: {
  selectedTrigger?: string | null
}) {
  const { control } = useFormContext()
  const { data: users } = useUsers()

  const userOptions = useMemo(() => {
    if (!users) return []
    return users.map((user) => ({
      value: user.id,
      label: `${user.first_name} ${user.last_name}`,
    }))
  }, [users])

  const filteredActionOptions = useMemo(
    () => getFilteredActionOptions(selectedTrigger),
    [selectedTrigger]
  )

  return (
    <>
      <Select
        name="action_type"
        control={control}
        placeholder="Choose action..."
        options={filteredActionOptions}
        className="flex-1"
      />
      <div className="flex items-center justify-center px-3 py-2 mt-3 bg-gray-100 rounded text-sm font-medium text-gray-700">
        TO
      </div>
      <Select
        name="target_user_id"
        control={control}
        placeholder="Choose user..."
        options={userOptions}
        className="flex-1"
      />
    </>
  )
}

// Component for "Add Tag" action - includes action + TO + tag
function AddTagAction({
  selectedTrigger,
}: {
  selectedTrigger?: string | null
}) {
  const { control } = useFormContext()
  const api = useApiService()
  const { data: tagOptions } = api.useGetTagsOptions()

  const filteredActionOptions = useMemo(
    () => getFilteredActionOptions(selectedTrigger),
    [selectedTrigger]
  )

  return (
    <>
      <Select
        name="action_type"
        control={control}
        placeholder="Choose action..."
        options={filteredActionOptions}
        className="flex-1"
      />
      <div className="flex items-center justify-center px-3 py-2 mt-3 bg-gray-100 rounded text-sm font-medium text-gray-700">
        TO
      </div>
      <Select
        name="target_tag_id"
        control={control}
        placeholder="Choose tag..."
        options={tagOptions}
        className="flex-1"
      />
    </>
  )
}

// Component for "Add to Campaign" action - includes action + TO + campaign
function AddToCampaignAction({
  selectedTrigger,
}: {
  selectedTrigger?: string | null
}) {
  const { control } = useFormContext()
  const api = useApiService()
  const { data: campaignOptions } = api.useGetCampaignsOptions()

  const filteredActionOptions = useMemo(
    () => getFilteredActionOptions(selectedTrigger),
    [selectedTrigger]
  )

  return (
    <>
      <Select
        name="action_type"
        control={control}
        placeholder="Choose action..."
        options={filteredActionOptions}
        className="flex-1"
      />
      <div className="flex items-center justify-center px-3 py-2 mt-3 bg-gray-100 rounded text-sm font-medium text-gray-700">
        TO
      </div>
      <Select
        name="target_campaign_id"
        control={control}
        placeholder="Choose campaign..."
        options={campaignOptions}
        className="flex-1"
      />
    </>
  )
}

// Component for "Add Note to Company" action - includes action + note input
function AddNoteToCompanyAction({
  automationId,
  selectedTrigger,
}: {
  automationId?: number
  selectedTrigger?: string | null
}) {
  const { control } = useFormContext()

  const filteredActionOptions = useMemo(
    () => getFilteredActionOptions(selectedTrigger),
    [selectedTrigger]
  )

  return (
    <div className="flex flex-col gap-4 w-full">
      <Select
        name="action_type"
        control={control}
        placeholder="Choose action..."
        options={filteredActionOptions}
        className="flex-1"
      />
      <AutomationNoteManager
        control={control}
        noteName="content"
        attachmentsName="attachments"
        label=""
        editorClassName="h-36 bg-white"
        enableMentions
        automationId={automationId}
      />
    </div>
  )
}

// Component for "Remove from Campaign" action - includes action + FROM + campaign
function RemoveFromCampaignAction({
  selectedTrigger,
}: {
  selectedTrigger?: string | null
}) {
  const { control } = useFormContext()
  const api = useApiService()
  const { data: campaignOptions } = api.useGetCampaignsOptions()

  const filteredActionOptions = useMemo(
    () => getFilteredActionOptions(selectedTrigger),
    [selectedTrigger]
  )

  return (
    <>
      <Select
        name="action_type"
        control={control}
        placeholder="Choose action..."
        options={filteredActionOptions}
        className="flex-1"
      />
      <div className="flex items-center justify-center px-3 py-2 mt-3 bg-gray-100 rounded text-sm font-medium text-gray-700">
        FROM
      </div>
      <Select
        name="target_campaign_id"
        control={control}
        placeholder="Choose campaign..."
        options={campaignOptions}
        className="flex-1"
      />
    </>
  )
}

// Default action dropdown when no specific action is selected
function DefaultActionSelect({
  selectedTrigger,
}: {
  selectedTrigger?: string | null
}) {
  const { control } = useFormContext()

  const filteredActionOptions = useMemo(
    () => getFilteredActionOptions(selectedTrigger),
    [selectedTrigger]
  )

  return (
    <Select
      name="action_type"
      control={control}
      placeholder="Choose action..."
      options={filteredActionOptions}
      className="flex-1"
    />
  )
}

// Main action renderer that switches based on action type
export function ActionRenderer({
  selectedAction,
  selectedTrigger,
  automationId,
}: ActionRendererProps) {
  switch (selectedAction) {
    case 'notify_user':
      return <NotifyUserAction selectedTrigger={selectedTrigger} />

    case 'change_sales_stage':
      return <ChangeSalesStageAction selectedTrigger={selectedTrigger} />

    case 'change_account_owner':
      return <ChangeAccountOwnerAction selectedTrigger={selectedTrigger} />

    case 'add_tag':
      return <AddTagAction selectedTrigger={selectedTrigger} />

    case 'add_to_campaign':
      return <AddToCampaignAction selectedTrigger={selectedTrigger} />

    case 'remove_from_campaign':
      return <RemoveFromCampaignAction selectedTrigger={selectedTrigger} />

    case 'add_company_note':
      return (
        <AddNoteToCompanyAction
          automationId={automationId}
          selectedTrigger={selectedTrigger}
        />
      )

    default:
      return <DefaultActionSelect selectedTrigger={selectedTrigger} />
  }
}
